import React, { useState, useEffect, useCallback } from "react"
import {
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Chip,
    FormControlLabel,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Switch,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import BlockIcon from "@mui/icons-material/Block"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as adminService from "../../services/adminService"
import { useAuth } from "../../services/AuthContext"
import { useCompanyId } from "../../hooks/useCompanyId"

const UsersAdminPage: React.FC = () => {
    const { t } = useTranslation()
    const { companyId: routeCompanyId } = useParams<{ companyId?: string }>()
    const { companyId: resolvedCompanyId } = useCompanyId()
    const companyId = routeCompanyId || resolvedCompanyId
    const { user } = useAuth() as any
    const [users, setUsers] = useState<any[]>([])
    const [outlets, setOutlets] = useState<any[]>([])
    const [registers, setRegisters] = useState<any[]>([])
    const [selectedOutletIds, setSelectedOutletIds] = useState<string[]>([])
    const [selectedRegisterIds, setSelectedRegisterIds] = useState<string[]>([])
    const [selectedRoleFilter, setSelectedRoleFilter] = useState("")
    const [selectedOutletFilter, setSelectedOutletFilter] = useState("")
    const [searchFilter, setSearchFilter] = useState("")
    const [formError, setFormError] = useState<string | null>(null)
    const [openDialog, setOpenDialog] = useState(false)
    const [editingUser, setEditingUser] = useState<any>(null)
    const [userPendingDeletion, setUserPendingDeletion] = useState<any>(null)

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        display_name: "",
        roles: ["cashier"] as string[],
        is_active: true,
    })

    const actorRoles: string[] = Array.isArray(user?.roles) ? user.roles : []
    const roleOptions = actorRoles.includes("superadmin")
        ? ["admin", "manager", "cashier", "webhook"]
        : actorRoles.includes("admin")
        ? ["admin", "manager", "cashier"]
        : actorRoles.includes("manager")
        ? ["cashier"]
        : []
    const listRoleOptions = actorRoles.includes("manager")
        ? ["cashier"]
        : ["admin", "manager", "cashier", "webhook"]

    const selectedRole = formData.roles[0] || "cashier"
    const canAssignRegisters =
        selectedRole === "cashier" || selectedRole === "manager"

    const visibleRegisters = registers.filter(
        (register) =>
            selectedOutletIds.length === 0 ||
            selectedOutletIds.includes(register.outlet_id)
    )

    const loadUsers = useCallback(async () => {
        if (!companyId) return
        try {
            const data = await adminService.getUsers(companyId, {
                role: selectedRoleFilter || undefined,
                outletId: selectedOutletFilter || undefined,
                search: searchFilter.trim() || undefined,
            })
            setUsers(data)
        } catch (error) {
            console.error("Failed to load users:", error)
        }
    }, [companyId, searchFilter, selectedOutletFilter, selectedRoleFilter])

    useEffect(() => {
        loadUsers()
    }, [loadUsers])

    const loadAssignmentOptions = useCallback(async () => {
        if (!companyId) return
        try {
            const [outletRows, registerRows] = await Promise.all([
                adminService.getCompanyOutlets(companyId),
                adminService.getCompanyRegisters(companyId),
            ])
            setOutlets(outletRows)
            setRegisters(registerRows)
        } catch (error) {
            console.error("Failed to load assignment options:", error)
        }
    }, [companyId])

    useEffect(() => {
        loadAssignmentOptions()
    }, [loadAssignmentOptions])

    const handleOpenDialog = (row?: any) => {
        setFormError(null)
        if (row) {
            setEditingUser(row)
            setFormData({
                email: row.email,
                password: "",
                display_name: row.display_name,
                roles: row.roles || ["cashier"],
                is_active: row.is_active,
            })

            if (companyId) {
                adminService
                    .getUserCashRegisters(companyId, row.id)
                    .then((assignments) => {
                        const registerIds = assignments.map(
                            (a: any) => a.cash_register_id
                        )
                        const outletIds: string[] = Array.from(
                            new Set<string>(
                                assignments
                                    .map((a: any) => a.outlet_id)
                                    .filter(
                                        (outletId: any): outletId is string =>
                                            typeof outletId === "string" &&
                                            outletId.length > 0
                                    )
                            )
                        )
                        setSelectedRegisterIds(registerIds)
                        setSelectedOutletIds(outletIds)
                    })
                    .catch((error) => {
                        console.error(
                            "Failed to load user cash-register assignments:",
                            error
                        )
                        setSelectedRegisterIds([])
                        setSelectedOutletIds([])
                    })
            }
        } else {
            setEditingUser(null)
            setFormData({
                email: "",
                password: "",
                display_name: "",
                roles: [roleOptions[0] || "cashier"],
                is_active: true,
            })
            setSelectedOutletIds([])
            setSelectedRegisterIds([])
        }
        setOpenDialog(true)
    }

    const handleDeactivateUser = async (userId: string) => {
        if (!companyId) return
        try {
            await adminService.deleteUser(companyId, userId)
            await loadUsers()
        } catch (error) {
            console.error("Failed to deactivate user:", error)
        }
    }

    const handleConfirmDeactivateUser = async () => {
        if (!userPendingDeletion?.id) return
        await handleDeactivateUser(userPendingDeletion.id)
        setUserPendingDeletion(null)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setEditingUser(null)
        setFormError(null)
    }

    const handleSave = async () => {
        if (!companyId) return
        try {
            setFormError(null)
            let savedUser
            if (editingUser) {
                savedUser = await adminService.updateUser(
                    companyId,
                    editingUser.id,
                    formData
                )
            } else {
                savedUser = await adminService.createUser(companyId, formData)
            }

            if (savedUser?.id && canAssignRegisters) {
                await adminService.setUserCashRegisters(
                    companyId,
                    savedUser.id,
                    selectedRegisterIds,
                    selectedOutletIds
                )
            }

            if (savedUser?.id && !canAssignRegisters && editingUser) {
                await adminService.setUserCashRegisters(
                    companyId,
                    savedUser.id,
                    [],
                    []
                )
            }

            handleCloseDialog()
            loadUsers()
        } catch (error: any) {
            setFormError(error?.message || t("usersAdmin.errors.saveError"))
            console.error("Failed to save user:", error)
        }
    }

    const selectSingleRole = (role: string) => {
        setFormData({ ...formData, roles: [role] })
        if (role !== "cashier" && role !== "manager") {
            setSelectedRegisterIds([])
        }
    }

    const toggleOutlet = (outletId: string) => {
        const nextOutletIds = selectedOutletIds.includes(outletId)
            ? selectedOutletIds.filter((id) => id !== outletId)
            : [...selectedOutletIds, outletId]

        setSelectedOutletIds(nextOutletIds)
        if (nextOutletIds.length === 0) return

        const allowedRegisters = new Set(
            registers
                .filter((register) =>
                    nextOutletIds.includes(register.outlet_id)
                )
                .map((register) => register.id)
        )
        setSelectedRegisterIds((prev) =>
            prev.filter((id) => allowedRegisters.has(id))
        )
    }

    const toggleRegister = (registerId: string) => {
        setSelectedRegisterIds((prev) =>
            prev.includes(registerId)
                ? prev.filter((id) => id !== registerId)
                : [...prev, registerId]
        )
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Typography variant="h4" gutterBottom>
                    {t("usersAdmin.title")}
                </Typography>

                <Box
                    sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t("usersAdmin.addUser")}
                    </Button>
                </Box>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr 2fr",
                            },
                        }}
                    >
                        <FormControl fullWidth size="small">
                            <InputLabel id="users-role-filter-label">
                                {t("usersAdmin.filters.role")}
                            </InputLabel>
                            <Select
                                labelId="users-role-filter-label"
                                label={t("usersAdmin.filters.role")}
                                value={selectedRoleFilter}
                                onChange={(event) =>
                                    setSelectedRoleFilter(event.target.value)
                                }
                            >
                                <MenuItem value="">
                                    {t("usersAdmin.filters.all")}
                                </MenuItem>
                                {listRoleOptions.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel id="users-outlet-filter-label">
                                {t("usersAdmin.filters.outlet")}
                            </InputLabel>
                            <Select
                                labelId="users-outlet-filter-label"
                                label={t("usersAdmin.filters.outlet")}
                                value={selectedOutletFilter}
                                onChange={(event) =>
                                    setSelectedOutletFilter(event.target.value)
                                }
                            >
                                <MenuItem value="">
                                    {t("usersAdmin.filters.all")}
                                </MenuItem>
                                {outlets.map((outlet) => (
                                    <MenuItem key={outlet.id} value={outlet.id}>
                                        {outlet.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={t("usersAdmin.filters.search")}
                            size="small"
                            value={searchFilter}
                            onChange={(event) =>
                                setSearchFilter(event.target.value)
                            }
                        />
                    </Box>
                </Paper>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t("usersAdmin.columns.email")}
                                </TableCell>
                                <TableCell>
                                    {t("usersAdmin.columns.displayName")}
                                </TableCell>
                                <TableCell>
                                    {t("usersAdmin.columns.roles")}
                                </TableCell>
                                <TableCell>
                                    {t("usersAdmin.columns.assignedOutlets")}
                                </TableCell>
                                <TableCell>
                                    {t("usersAdmin.columns.status")}
                                </TableCell>
                                <TableCell>
                                    {t("usersAdmin.columns.actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>{row.display_name}</TableCell>
                                    <TableCell>
                                        {row.roles?.map((role: string) => (
                                            <Chip
                                                key={role}
                                                label={role}
                                                size="small"
                                                sx={{ mr: 0.5 }}
                                            />
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        {Array.isArray(
                                            row.assigned_outlet_names
                                        ) &&
                                        row.assigned_outlet_names.length > 0
                                            ? row.assigned_outlet_names.map(
                                                  (outletName: string) => (
                                                      <Chip
                                                          key={`${row.id}-${outletName}`}
                                                          label={outletName}
                                                          size="small"
                                                          sx={{
                                                              mr: 0.5,
                                                              mb: 0.5,
                                                          }}
                                                      />
                                                  )
                                              )
                                            : t("common.dash")}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={
                                                row.is_active
                                                    ? t(
                                                          "usersAdmin.status.active"
                                                      )
                                                    : t(
                                                          "usersAdmin.status.inactive"
                                                      )
                                            }
                                            size="small"
                                            color={
                                                row.is_active
                                                    ? "success"
                                                    : "default"
                                            }
                                            variant={
                                                row.is_active
                                                    ? "filled"
                                                    : "outlined"
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() =>
                                                handleOpenDialog(row)
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        {row.is_active ? (
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    setUserPendingDeletion(row)
                                                }
                                                title={
                                                    t(
                                                        "usersAdmin.actions.deactivate"
                                                    ) || "Désactiver"
                                                }
                                            >
                                                <BlockIcon />
                                            </IconButton>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingUser
                            ? t("usersAdmin.dialog.editTitle")
                            : t("usersAdmin.dialog.addTitle")}
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            {formError ? (
                                <Alert severity="error">{formError}</Alert>
                            ) : null}

                            <TextField
                                label={t("usersAdmin.fields.email")}
                                type="email"
                                required
                                fullWidth
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        email: e.target.value,
                                    })
                                }
                                disabled={!!editingUser}
                            />
                            <TextField
                                label={
                                    editingUser
                                        ? t("usersAdmin.fields.newPassword")
                                        : t("usersAdmin.fields.password")
                                }
                                type="password"
                                required={!editingUser}
                                fullWidth
                                value={formData.password}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        password: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label={t("usersAdmin.fields.displayName")}
                                required
                                fullWidth
                                value={formData.display_name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        display_name: e.target.value,
                                    })
                                }
                            />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t("usersAdmin.fields.roles")}
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {roleOptions.map((role) => (
                                        <Chip
                                            key={role}
                                            label={role}
                                            onClick={() =>
                                                selectSingleRole(role)
                                            }
                                            color={
                                                formData.roles.includes(role)
                                                    ? "primary"
                                                    : "default"
                                            }
                                        />
                                    ))}
                                </Box>
                            </Box>

                            {canAssignRegisters ? (
                                <>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                        >
                                            {t(
                                                "usersAdmin.fields.authorizedOutlets"
                                            )}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {outlets.map((outlet) => (
                                                <Chip
                                                    key={outlet.id}
                                                    label={outlet.name}
                                                    onClick={() =>
                                                        toggleOutlet(outlet.id)
                                                    }
                                                    color={
                                                        selectedOutletIds.includes(
                                                            outlet.id
                                                        )
                                                            ? "primary"
                                                            : "default"
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                        >
                                            {t(
                                                "usersAdmin.fields.authorizedRegisters"
                                            )}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {visibleRegisters.map(
                                                (register) => (
                                                    <Chip
                                                        key={register.id}
                                                        label={`${
                                                            register.outlet_name ||
                                                            t(
                                                                "usersAdmin.outletFallback"
                                                            )
                                                        } • ${register.name}`}
                                                        onClick={() =>
                                                            toggleRegister(
                                                                register.id
                                                            )
                                                        }
                                                        color={
                                                            selectedRegisterIds.includes(
                                                                register.id
                                                            )
                                                                ? "primary"
                                                                : "default"
                                                        }
                                                    />
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                </>
                            ) : null}

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                is_active: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={t("usersAdmin.fields.active")}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            {t("common.cancel")}
                        </Button>
                        <Button onClick={handleSave} variant="contained">
                            {t("common.save")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={Boolean(userPendingDeletion)}
                    onClose={() => setUserPendingDeletion(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        {t("usersAdmin.deleteDialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t("usersAdmin.deleteDialog.message")}{" "}
                            <strong>
                                {userPendingDeletion?.display_name ||
                                    userPendingDeletion?.email}
                            </strong>{" "}
                            ?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUserPendingDeletion(null)}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={handleConfirmDeactivateUser}
                        >
                            {t("usersAdmin.deleteDialog.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default UsersAdminPage
